/**
 * URL安全验证工具函数
 */

/**
 * 检查URL是否安全
 * @param {string} url 需要验证的URL
 * @param {Object} options 验证选项
 * @param {Array<string>} options.allowedDomains 允许的域名列表
 * @param {Array<string>} options.allowedProtocols 允许的协议列表
 * @param {Array<string>} options.allowedExtensions 允许的文件扩展名列表
 * @returns {boolean} 是否安全
 */
export function isUrlSafe(url, options = {}) {
  if (!url || typeof url !== 'string') {
    return false;
  }

  // 默认选项
  const defaultOptions = {
    allowedDomains: [], // 允许的域名，空数组表示不限制
    allowedProtocols: ['https:', 'http:'], // 默认只允许http和https协议
    allowedExtensions: ['.mp4', '.webm', '.ogg', '.mov'] // 默认允许的视频扩展名
  };

  const opts = { ...defaultOptions, ...options };

  try {
    // 尝试解析URL
    const urlObj = new URL(url);
    
    // 检查协议
    if (!opts.allowedProtocols.includes(urlObj.protocol)) {
      console.warn('不安全的URL协议:', urlObj.protocol);
      return false;
    }
    
    // 检查域名（如果有设置白名单）
    if (opts.allowedDomains.length > 0 && !opts.allowedDomains.some(domain => 
      urlObj.hostname === domain || urlObj.hostname.endsWith('.' + domain))) {
      console.warn('不在白名单内的域名:', urlObj.hostname);
      return false;
    }
    
    // 检查文件扩展名（如果有路径）
    if (urlObj.pathname) {
      const hasAllowedExtension = opts.allowedExtensions.some(ext => 
        urlObj.pathname.toLowerCase().endsWith(ext));
      
      if (!hasAllowedExtension) {
        console.warn('不支持的文件类型:', urlObj.pathname);
        return false;
      }
    }
    
    // 基本的XSS检查
    if (url.includes('javascript:') || url.includes('data:') || url.includes('vbscript:')) {
      console.warn('可能的XSS攻击尝试:', url);
      return false;
    }
    
    return true;
  } catch (e) {
    console.error('URL验证失败:', e);
    return false;
  }
}

/**
 * 获取安全的视频URL，不安全则返回默认URL
 * @param {string} url 需要验证的URL
 * @param {string} defaultUrl 默认URL
 * @param {Object} options 验证选项
 * @returns {string} 安全的URL或默认URL
 */
export function getSafeVideoUrl(url, defaultUrl = '', options = {}) {
  return isUrlSafe(url, options) ? url : defaultUrl;
}

/**
 * 通过安全验证处理视频列表数据
 * @param {Array} list 视频列表
 * @param {string} attachmentKey 包含URL的属性名
 * @param {string} defaultUrl 默认URL
 * @param {Object} options 验证选项
 * @returns {Array} 处理后的安全列表
 */
export function sanitizeVideoList(list, attachmentKey = 'attachment', defaultUrl = '', options = {}) {
  if (!Array.isArray(list)) {
    return [];
  }
  
  return list.map(item => {
    if (item && item[attachmentKey]) {
      return {
        ...item,
        [attachmentKey]: getSafeVideoUrl(item[attachmentKey], defaultUrl, options)
      };
    }
    return item;
  });
} 